import { useEffect, useState } from 'react'
import { Button, Input, Popover } from '@heroui/react'
import { api } from '../lib/api'
import { keyedAddress } from '../lib/equipment'
import { screenPath } from '../lib/screenKind'
import { explain } from '../lib/staffErrors'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { copyWhenReady, linkOffer, type KeyState, type LinkRow } from './linkRows'

/**
 * The editor header's Link: the machines showing this design, each with its
 * screen address to copy, or a new keyed one. A key lives only in this list
 * until it closes.
 */
export function MachineLinks({
  rows,
  known,
  onUse,
  onNotify,
}: {
  rows: readonly LinkRow[]
  known: boolean
  onUse: () => void
  onNotify: (text: string) => void
}) {
  const { say, lang } = useStaffText()
  const [open, setOpen] = useState(false)
  const [keys, setKeys] = useState<Record<string, KeyState>>({})
  // An address the clipboard refused (plain http on a LAN has none), to copy by hand.
  const [shown, setShown] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const close = () => {
    setOpen(false)
    setShown({})
    setErrors({})
  }

  const ids = rows.map((r) => r.unit.id).join(',')
  useEffect(() => {
    if (!open || !ids) return
    let live = true
    for (const id of ids.split(',')) {
      api
        .screenKeyStatus(id)
        .then((s) => live && setKeys((k) => ({ ...k, [id]: { hasKey: s.hasKey, required: s.required } })))
        .catch((err) => live && setErrors((e) => ({ ...e, [id]: explain(err, lang, 'common.unreachable') })))
    }
    return () => {
      live = false
    }
  }, [open, ids, lang])

  const copyPlain = async (row: LinkRow) => {
    const url = window.location.origin + screenPath(row.unit.kind, row.branchCode, row.unit.code)
    if (await copyWhenReady(Promise.resolve(url))) onNotify(say('designs.link.copied'))
    else setShown((s) => ({ ...s, [row.unit.id]: url }))
  }

  // One click: the new key replaces the old at once, and its address is copied.
  const issue = async (row: LinkRow) => {
    const id = row.unit.id
    setBusy(id)
    setErrors((e) => ({ ...e, [id]: '' }))
    const address = api.issueScreenKey(id).then((r) => keyedAddress(r.url, r.path, window.location.origin))
    const copying = copyWhenReady(address)
    try {
      const url = await address
      setKeys((k) => ({ ...k, [id]: { hasKey: true, required: k[id]?.required ?? false } }))
      if (await copying) onNotify(say('designs.link.copied'))
      else setShown((s) => ({ ...s, [id]: url }))
    } catch (err) {
      setErrors((e) => ({ ...e, [id]: explain(err, lang, 'common.unreachable') }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Popover isOpen={open} onOpenChange={(on) => (on ? setOpen(true) : close())}>
      <Button variant="secondary" size="sm" isDisabled={!known}>
        <Icon name="link" size={14} /> {say('designs.link.button')}
      </Button>
      <Popover.Content placement="bottom end" className="studio-links">
        <Popover.Dialog aria-label={say('designs.link.title')}>
          {rows.length === 0 ? (
            <div className="studio-links__none">
              <span>{say('designs.page.notUsed')}</span>
              <Button
                variant="secondary"
                size="sm"
                onPress={() => {
                  close()
                  onUse()
                }}
              >
                {say('eq.design.use')}
              </Button>
            </div>
          ) : (
            <ul>
              {rows.map((row) => {
                const id = row.unit.id
                const offer = linkOffer(keys[id])
                return (
                  <li key={id}>
                    <div className="studio-links__row">
                      <span className="studio-links__name" title={row.label}>
                        {row.label}
                      </span>
                      {offer.copy && (
                        <Button variant="secondary" size="sm" onPress={() => void copyPlain(row)}>
                          <Icon name="copy" size={14} /> {say('designs.link.copy')}
                        </Button>
                      )}
                      <Button variant="secondary" size="sm" isDisabled={!keys[id] || busy !== null} onPress={() => void issue(row)}>
                        <Icon name="key" size={14} /> {say('designs.link.new')}
                      </Button>
                    </div>
                    {shown[id] && (
                      <Input
                        variant="secondary"
                        fullWidth
                        className="studio-links__url"
                        readOnly
                        value={shown[id]}
                        aria-label={say('designs.link.address')}
                        onFocus={(e) => e.currentTarget.select()}
                        autoFocus
                      />
                    )}
                    {errors[id] && <p className="studio-field__error">{errors[id]}</p>}
                  </li>
                )
              })}
            </ul>
          )}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  )
}
